/**
 * PixiJS-rendering. Läser sim-state, skriver aldrig. Interpolerar mellan de två
 * senaste sim-ticksen för mjuk rörelse vid 60 fps mot simens 20 tps.
 * Fast kamera: hela kartan alltid synlig (beslut 2026-07-15), letterboxad.
 * Sprites poolas per entity-id — inga allokeringar i render-loopen i steady state.
 */
import { Application, Container, Graphics } from 'pixi.js';
import type { SimConfig } from '../sim/config';
import { fpToWorld } from '../sim/fixed';
import type { GameState, PlayerId } from '../sim/state';
import { PLAYER_IDS } from '../sim/state';
import {
  defaultSkinSelection,
  getSkin,
  PLAYER_COLORS,
  type PlayerSkinSelection,
  type UnitVisual,
} from './skins';

interface EntitySprite {
  gfx: Graphics;
  prevX: number;
  prevY: number;
  currX: number;
  currY: number;
  seen: boolean;
}

function drawVisual(gfx: Graphics, visual: UnitVisual, owner: PlayerId, scale: number): void {
  const fill = visual.fillColor ?? PLAYER_COLORS[owner].fill;
  const outline = visual.outlineColor ?? PLAYER_COLORS[owner].outline;
  const r = visual.radius * scale;
  gfx.clear();
  switch (visual.shape) {
    case 'circle':
      gfx.circle(0, 0, r);
      break;
    case 'triangle':
      gfx.poly([0, -r, r * 0.87, r * 0.5, -r * 0.87, r * 0.5]);
      break;
    case 'square':
      gfx.rect(-r * 0.85, -r * 0.85, r * 1.7, r * 1.7);
      break;
    case 'diamond':
      gfx.poly([0, -r, r * 0.7, 0, 0, r, -r * 0.7, 0]);
      break;
    case 'pentagon': {
      const pts: number[] = [];
      for (let i = 0; i < 5; i++) {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
        pts.push(Math.cos(a) * r, Math.sin(a) * r);
      }
      gfx.poly(pts);
      break;
    }
    case 'wing':
      gfx.poly([0, -r, r, r * 0.6, 0, r * 0.1, -r, r * 0.6]);
      break;
  }
  gfx.fill(fill);
  gfx.stroke({ width: Math.max(1, scale * 0.06), color: outline });
}

export class Renderer {
  private app!: Application;
  private world!: Container;
  private unitLayer!: Container;
  private config: SimConfig;
  private skins: Record<PlayerId, PlayerSkinSelection>;
  /** Pixels per world unit (sätts vid resize). */
  private scale = 10;
  private unitSprites = new Map<number, EntitySprite>();
  private shipSprites = new Map<PlayerId, EntitySprite>();

  constructor(config: SimConfig) {
    this.config = config;
    this.skins = { p1: defaultSkinSelection(), p2: defaultSkinSelection() };
  }

  async init(host: HTMLElement): Promise<void> {
    this.app = new Application();
    await this.app.init({ background: 0x0b1120, resizeTo: host, antialias: true });
    host.appendChild(this.app.canvas);

    this.world = new Container();
    this.app.stage.addChild(this.world);
    this.unitLayer = new Container();

    this.layout();
    this.drawStatics();
    this.world.addChild(this.unitLayer);
    this.app.renderer.on('resize', () => {
      this.layout();
      this.redrawAll();
    });
  }

  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  /** Skärmkoordinat → world-koordinat (world units, flyttal). */
  screenToWorld(px: number, py: number): { x: number; y: number } {
    return { x: (px - this.world.x) / this.scale, y: (py - this.world.y) / this.scale };
  }

  private layout(): void {
    const { width, height } = this.config.match.mapSize;
    const sw = this.app.renderer.width;
    const sh = this.app.renderer.height;
    this.scale = Math.min(sw / width, sh / height);
    this.world.x = (sw - width * this.scale) / 2;
    this.world.y = (sh - height * this.scale) / 2;
  }

  private drawStatics(): void {
    const { width, height } = this.config.match.mapSize;
    const bg = new Graphics();
    bg.rect(0, 0, width * this.scale, height * this.scale).fill(0x111c33);
    bg.stroke({ width: 2, color: 0x2c3e5f });
    // Diskret rutnät för rumskänsla.
    for (let x = 8; x < width; x += 8)
      bg.moveTo(x * this.scale, 0)
        .lineTo(x * this.scale, height * this.scale)
        .stroke({ width: 1, color: 0x16223d });
    for (let y = 8; y < height; y += 8)
      bg.moveTo(0, y * this.scale)
        .lineTo(width * this.scale, y * this.scale)
        .stroke({ width: 1, color: 0x16223d });
    this.world.addChildAt(bg, 0);

    for (const id of PLAYER_IDS) {
      const pos = this.config.match.basePositions[id];
      const visual = getSkin(this.skins[id].baseSkin).baseVisual();
      const gfx = new Graphics();
      drawVisual(gfx, visual, id, this.scale);
      gfx.x = pos.x * this.scale;
      gfx.y = pos.y * this.scale;
      this.world.addChild(gfx);
    }
  }

  private redrawAll(): void {
    // Vid resize: rensa och rita om statiska lager + sprites i ny skala.
    for (const child of this.world.removeChildren()) child.destroy({ children: true });
    this.unitSprites.clear();
    this.shipSprites.clear();
    this.unitLayer = new Container();
    this.drawStatics();
    this.world.addChild(this.unitLayer);
  }

  /** Anropas efter varje sim-tick: flytta fram interpolationsfönstret. */
  onTick(state: GameState): void {
    for (const sprite of this.unitSprites.values()) sprite.seen = false;

    for (const unit of state.units) {
      let sprite = this.unitSprites.get(unit.id);
      if (!sprite) {
        const skinId = this.skins[unit.owner].unitSkinByFamily[unit.family];
        const visual = getSkin(skinId).unitVisual(unit.family, unit.tier);
        const gfx = new Graphics();
        drawVisual(gfx, visual, unit.owner, this.scale);
        this.unitLayer.addChild(gfx);
        const x = fpToWorld(unit.x);
        const y = fpToWorld(unit.y);
        sprite = { gfx, prevX: x, prevY: y, currX: x, currY: y, seen: true };
        this.unitSprites.set(unit.id, sprite);
      }
      sprite.prevX = sprite.currX;
      sprite.prevY = sprite.currY;
      sprite.currX = fpToWorld(unit.x);
      sprite.currY = fpToWorld(unit.y);
      sprite.seen = true;
    }

    for (const [id, sprite] of this.unitSprites) {
      if (!sprite.seen) {
        sprite.gfx.destroy();
        this.unitSprites.delete(id);
      }
    }

    for (const id of PLAYER_IDS) {
      const ship = state.motherships[id];
      let sprite = this.shipSprites.get(id);
      if (!sprite) {
        const visual = getSkin(this.skins[id].mothershipSkin).mothershipVisual();
        const gfx = new Graphics();
        drawVisual(gfx, visual, id, this.scale);
        this.world.addChild(gfx);
        const x = fpToWorld(ship.x);
        const y = fpToWorld(ship.y);
        sprite = { gfx, prevX: x, prevY: y, currX: x, currY: y, seen: true };
        this.shipSprites.set(id, sprite);
      }
      sprite.prevX = sprite.currX;
      sprite.prevY = sprite.currY;
      sprite.currX = fpToWorld(ship.x);
      sprite.currY = fpToWorld(ship.y);
    }
  }

  /** Anropas varje frame med alpha ∈ [0,1] = andel av nästa tick som passerat. */
  render(alpha: number): void {
    for (const sprite of this.unitSprites.values()) {
      sprite.gfx.x = (sprite.prevX + (sprite.currX - sprite.prevX) * alpha) * this.scale;
      sprite.gfx.y = (sprite.prevY + (sprite.currY - sprite.prevY) * alpha) * this.scale;
    }
    for (const sprite of this.shipSprites.values()) {
      sprite.gfx.x = (sprite.prevX + (sprite.currX - sprite.prevX) * alpha) * this.scale;
      sprite.gfx.y = (sprite.prevY + (sprite.currY - sprite.prevY) * alpha) * this.scale;
    }
  }
}
